import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  Bot,
  Download,
  FileText,
  Menu,
  Moon,
  Search,
  Sun,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/features/platform/contexts/auth-context";
import { useTheme } from "@/contexts/theme-context";
import { cn } from "@/utils";
import { downloadPlatformSkill, getPlatformDocs } from "./api";
import { DocsUserMenu } from "./docs-user-menu";
import { DOCS_BASE, slugify } from "./paths";
import type { DocBlock, DocPage, PlatformDocs } from "./types";

export type PlatformDocsContext = { docs: PlatformDocs };

const blockText = (block: DocBlock): string => {
  switch (block.type) {
    case "p":
    case "h":
      return block.text;
    case "list":
      return block.items.join(" ");
    case "steps":
      return block.items.map((i) => `${i.title} ${i.text}`).join(" ");
    case "table":
      return [...block.columns, ...block.rows.flat()].join(" ");
    case "callout":
      return `${block.title ?? ""} ${block.text}`;
    case "endpoint":
      return `${block.path} ${block.summary}`;
    case "flow":
      return block.steps.map((s) => s.text).join(" ");
    case "facts":
      return block.items.map((i) => `${i.label} ${i.value}`).join(" ");
    case "timeline":
      return block.items.map((i) => `${i.title} ${i.text}`).join(" ");
    case "code":
      return block.code;
    case "code_tabs":
      return block.tabs.map((t) => t.code).join(" ");
    default:
      return "";
  }
};

const Spinner = () => (
  <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-950">
    <div className="w-8 h-8 rounded-full border-2 border-slate-200 dark:border-neutral-800 border-t-orange-500 animate-spin" />
  </div>
);

export const PlatformDocsLayout = () => {
  const { user, isLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();

  const [docs, setDocs] = useState<PlatformDocs | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [downloading, setDownloading] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!user) return;
    getPlatformDocs()
      .then(setDocs)
      .catch(() => setLoadError("Could not load the documentation."));
  }, [user]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setQuery("");
  }, [location.pathname]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!docs || q.length < 2) return [];
    return docs.groups
      .flatMap((g) => g.pages)
      .filter(
        (p: DocPage) =>
          p.title.toLowerCase().includes(q) ||
          p.summary.toLowerCase().includes(q) ||
          p.blocks.some((b) => blockText(b).toLowerCase().includes(q))
      );
  }, [docs, query]);

  if (isLoading) return <Spinner />;

  if (!user) {
    const returnUrl = location.pathname + location.search + location.hash;
    try {
      sessionStorage.setItem("auth_return_url", returnUrl);
    } catch {
      /* storage unavailable */
    }
    return <Navigate to={`/auth/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadPlatformSkill();
    } catch {
      toast.error("Could not download the skill package.");
    } finally {
      setDownloading(false);
    }
  };

  const isDark = theme === "dark";
  const activePageId = location.pathname.replace(DOCS_BASE, "").split("/").filter(Boolean)[0] ?? docs?.groups[0]?.pages[0]?.id;

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-full w-(--sidebar-width) flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 transition-transform duration-300 lg:static lg:translate-x-0",
          menuOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-900 p-5">
          <Link to="/" className="flex items-center gap-2.5">
            <img src="/brand/light-logo-with-text.svg" alt="Platform" className="h-7 w-auto dark:hidden" />
            <img src="/brand/dark-logo-with-text.svg" alt="Platform" className="hidden h-7 w-auto dark:block" />
          </Link>
          <button
            onClick={() => setMenuOpen(false)}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 lg:hidden"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="flex-1 space-y-7 overflow-y-auto px-4 py-5 no-scrollbar">
          {docs?.groups.map((group) => (
            <div key={group.id}>
              <div className="px-3 pb-2 text-[11px] font-bold uppercase tracking-widest text-slate-900 dark:text-white">
                {group.title}
              </div>
              <div className="ml-3 border-l border-slate-200 dark:border-slate-800">
                {group.pages.map((page) => {
                  const isActive = page.id === activePageId;
                  const sections = page.blocks.filter(
                    (b): b is Extract<DocBlock, { type: "h" }> => b.type === "h"
                  );
                  return (
                    <div key={page.id}>
                      <Link
                        to={`${DOCS_BASE}/${page.id}`}
                        className={cn(
                          "-ml-px block border-l py-1.5 pl-4 pr-3 text-[13px] transition-colors",
                          isActive
                            ? "border-orange-500 bg-orange-50/70 font-semibold text-orange-600 dark:bg-orange-500/10 dark:text-orange-400"
                            : "border-transparent font-medium text-slate-600 hover:border-slate-300 hover:text-slate-900 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-white"
                        )}
                      >
                        {page.title}
                      </Link>
                      {isActive && page.nav_sections !== false && sections.length > 0 && (
                        <div className="bg-slate-50/70 py-1 dark:bg-slate-900/40">
                          {sections.map((section) => (
                            <Link
                              key={section.text}
                              to={`${DOCS_BASE}/${page.id}#${slugify(section.text)}`}
                              className="-ml-px block border-l border-transparent py-1 pl-8 pr-3 text-[12px] text-slate-500 transition-colors hover:border-slate-400 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                            >
                              {section.text}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-slate-100 dark:border-slate-900 bg-slate-50/50 dark:bg-slate-900/20 p-4">
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="flex w-full items-start gap-3 rounded-2xl border border-orange-200 dark:border-orange-500/30 bg-orange-50 dark:bg-orange-500/10 p-3 text-left transition-colors hover:bg-orange-100 dark:hover:bg-orange-500/15 disabled:opacity-60"
          >
            <Bot className="mt-0.5 h-5 w-5 shrink-0 text-orange-500" />
            <span>
              <span className="block text-[13px] font-semibold text-slate-900 dark:text-white">
                Agent skill package
              </span>
              <span className="block text-[11.5px] leading-4 text-slate-500 dark:text-slate-400">
                Works with any AI coding agent
              </span>
            </span>
          </button>
        </div>
      </aside>

      <main className="flex h-full flex-1 flex-col overflow-hidden">
        <header className="relative z-30 flex h-16 shrink-0 items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 bg-white/90 dark:bg-slate-950/90 px-4 backdrop-blur lg:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <button
              onClick={() => setMenuOpen(true)}
              className="rounded-md p-2 text-slate-500 transition-colors hover:bg-slate-100 dark:hover:bg-slate-900 lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="truncate text-sm text-slate-500 dark:text-slate-400">
              Developer docs <span className="px-1 text-slate-300 dark:text-slate-700">/</span>
              <span className="font-medium text-orange-500">Platform integration</span>
            </span>
          </div>

          <div className="flex items-center gap-2 lg:gap-4">
            <div className="relative hidden w-48 sm:block lg:w-72">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-slate-400">
                <Search className="h-4 w-4" />
              </span>
              <input
                ref={searchRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search... (Ctrl+K)"
                className="w-full rounded-lg border border-slate-200 bg-slate-50 py-1.5 pl-10 pr-12 text-sm outline-none transition-all focus:border-orange-500/60 dark:border-slate-800 dark:bg-slate-900 dark:text-white"
              />
              <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                <kbd className="hidden h-5 select-none items-center gap-0.5 rounded border border-slate-200 bg-white px-1.5 font-mono text-[10px] font-medium text-slate-400 dark:border-slate-800 dark:bg-slate-950 lg:inline-flex">
                  <span>Ctrl</span>
                  <span>K</span>
                </kbd>
              </div>

              {query.trim().length >= 2 && (
                <div className="absolute right-0 top-11 z-50 w-80 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-800 dark:bg-slate-900">
                  {results.length === 0 ? (
                    <p className="px-4 py-3 text-[13px] text-slate-500">No results for “{query}”.</p>
                  ) : (
                    results.map((page) => (
                      <Link
                        key={page.id}
                        to={`${DOCS_BASE}/${page.id}`}
                        className="flex items-start gap-3 px-4 py-3 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/60"
                      >
                        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                        <span>
                          <span className="block text-[13px] font-semibold text-slate-900 dark:text-white">
                            {page.title}
                          </span>
                          <span className="block text-[12px] text-slate-500 dark:text-slate-400">
                            {page.summary}
                          </span>
                        </span>
                      </Link>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="hidden items-center gap-1.5 rounded-xl border border-slate-200 dark:border-slate-800 px-3.5 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-50 dark:hover:bg-slate-900 disabled:opacity-60 md:flex"
            >
              <Download className="h-3.5 w-3.5" />
              Skill package
            </button>
            <button
              onClick={toggleTheme}
              className="rounded-xl border border-slate-200 dark:border-slate-800 p-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-900 dark:hover:text-slate-200"
              title="Toggle theme"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <DocsUserMenu />
          </div>
        </header>

        <div className="flex-1 overflow-y-auto no-scrollbar">
          {loadError ? (
            <p className="p-12 text-center text-sm text-slate-500">{loadError}</p>
          ) : docs ? (
            <Outlet context={{ docs } satisfies PlatformDocsContext} />
          ) : (
            <div className="flex justify-center p-24">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 dark:border-neutral-800 border-t-orange-500" />
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PlatformDocsLayout;
